import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { JsonApiWrap } from '../../shared/models/JsonApiWrap';
import { BaseRestService } from '../../shared/services/BaseRestService';
import { IntegrationService } from '../../shared/services/integration.service';
import { HeadersService } from '../../shared/utilities/HeadersService';
import { WireDetailsApiResponse } from '../models/wire/WireDetailsApiResponse';
import { WireDetailsRequest } from '../models/wire/WireDetailsRequest';
import { WireInputRequest } from '../models/wire/WireInputRequest';
import { WireVerifyRequestV3 } from '../models/wire/WireVerifyRequestV3';
import { WireVerifyResponseV3 } from '../models/wire/WireVerifyResponseV3';
import { WireAcknowledgeRequestV3 } from '../models/wire/WireAcknowledgeRequestV3';
import { WireAcknowledgeResponseV3 } from '../models/wire/WireAcknowledgeResponseV3';
import { WireInputResponseV3 } from '../models/wire/WireInputResponseV3';

@Injectable({
  providedIn: 'root',
})
export class WireService {
  acknowledgementConsentEnabled: boolean;

  constructor(
    private http: HttpClient,
    private headersService: HeadersService,
    private baseService: BaseRestService,
    private integrationService: IntegrationService
  ) { }

  mmExperienceApiPath: string = 'api/ria.MoneyMovementExperience';

  callWireInputV3(
    requestData: WireInputRequest,
    isPalAccountInContext: boolean = false
  ): Observable<WireInputResponseV3> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.mmExperienceApiPath +
      '/v1/WireInputV3';
    let requestBody: JsonApiWrap<WireInputRequest> = {
      data: {
        type: 'wire-input-request-v3',
        id: '',
        attributes: requestData,
      },
    };
    let headers =
      this.headersService.createHeadersWithJsonApiContentSharedToken('1');
    if (isPalAccountInContext) {
      headers['Account-Type'] = 'PALACCOUNT';
    }

    return this.http
      .post<{ meta: WireInputResponseV3 }>(serviceUrl, requestBody, { headers })
      .pipe(map((res) => res.meta));
  }

  callWireVerifyV3(
    requestData: WireVerifyRequestV3,
    isPalAccountInContext: boolean = false
  ): Observable<WireVerifyResponseV3> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.mmExperienceApiPath +
      '/v1/WireVerificationV3';
    let requestBody: JsonApiWrap<WireVerifyRequestV3> = {
      data: {
        type: 'wire-verification-request-v3',
        id: '',
        attributes: requestData,
      },
    };
    let headers =
      this.headersService.createHeadersWithJsonApiContentSharedToken('1');
    if (isPalAccountInContext) {
      headers['Account-Type'] = 'PALACCOUNT';
    }

    return this.baseService.post<WireVerifyRequestV3, WireVerifyResponseV3>(
      serviceUrl,
      headers,
      requestBody,
      true
    );
  }

  callWireDetails(
    requestData: WireDetailsRequest,
    schwabResourceVersion: string = '1',
    isPalAccountInContext: boolean = false,
  ): Observable<WireDetailsApiResponse> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.mmExperienceApiPath +
      '/v1/Details';
    let requestBody: JsonApiWrap<WireDetailsRequest> = {
      data: {
        type: 'bank-details',
        id: '',
        attributes: requestData,
      },
    };
    let headers =
      this.headersService.createHeadersWithJsonApiContentSharedToken(schwabResourceVersion);
    if (isPalAccountInContext) {
      headers['Account-Type'] = 'PALACCOUNT';
    }

    return this.baseService.post<WireDetailsRequest, WireDetailsApiResponse>(
      serviceUrl,
      headers,
      requestBody,
      true
    );
  }

  callWireAcknowledgeV3(
    requestData: WireAcknowledgeRequestV3,
    isPalAccountInContext: boolean = false
  ): Observable<WireAcknowledgeResponseV3> {
    let serviceUrl =
      this.integrationService.JfkEnv.BaseUrl +
      this.mmExperienceApiPath +
      '/v1/WireV3';
    let requestBody: JsonApiWrap<WireAcknowledgeRequestV3> = {
      data: {
        type: 'wire-v3',
        id: '',
        attributes: requestData,
      },
    };
    let headers =
      this.headersService.createHeadersWithJsonApiContentSharedToken('3');
    headers['Acknowledgement-Consent'] = 'true';
    if (isPalAccountInContext) {
      headers['Account-Type'] = 'PALACCOUNT';
    }

    return this.baseService.post<
      WireAcknowledgeRequestV3,
      WireAcknowledgeResponseV3
    >(serviceUrl, headers, requestBody, true);
  }
}
